import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { type Editor, EditorContent, type JSONContent, useEditor } from '@tiptap/react';
import cn from 'classnames';
import { ClientOnly } from 'remix-utils/client-only';

import '~/routes/community/_components/communityEditor.css';

import useMentionSuggestions from '~/routes/community/_hooks/useMentionSuggestions';
import { isTiptapContentEmpty } from '~/routes/community/_utils/isTiptapContentEmpty';

import { extensions } from './extensions';

export interface CommentEditorHandle {
  getJSON: () => JSONContent;
  clear: () => void;
  isEmpty: () => boolean;
}

interface CommentEditorProps {
  placeholder: string;
  initialContent?: JSONContent | null;
  autofocus?: boolean;
  className?: string;
  onEmptyChange?: (isEmpty: boolean) => void;
  onEditorReady?: (editor: Editor | null) => void;
  onKeyboardSubmit?: () => void;
  spaceSlug?: string;
}

const emptyDoc: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] };

const isEditorEmpty = (editor: Editor | null): boolean => !editor || isTiptapContentEmpty(editor.getJSON());

const TiptapEditor = forwardRef<CommentEditorHandle, CommentEditorProps>(
  (
    { placeholder, initialContent, autofocus, className, onEmptyChange, onEditorReady, onKeyboardSubmit, spaceSlug },
    ref,
  ) => {
    const onEmptyChangeRef = useRef(onEmptyChange);
    onEmptyChangeRef.current = onEmptyChange;
    const onEditorReadyRef = useRef(onEditorReady);
    onEditorReadyRef.current = onEditorReady;
    const onKeyboardSubmitRef = useRef(onKeyboardSubmit);
    onKeyboardSubmitRef.current = onKeyboardSubmit;
    const lastEmptyRef = useRef(true);
    const mentionSuggestions = useMentionSuggestions({ spaceSlug });
    const mentionSuggestionsRef = useRef(mentionSuggestions);
    mentionSuggestionsRef.current = mentionSuggestions;
    const { t } = useTranslation();

    const editor = useEditor({
      extensions: extensions({
        placeholder,
        onKeyboardSubmit: () => onKeyboardSubmitRef.current?.(),
        mention: {
          getSuggestions: () => mentionSuggestionsRef.current,
          fallbackLabel: t('community.join.former_member'),
          listLabel: t('community.mentions.list_label'),
        },
      }),
      content: initialContent ?? emptyDoc,
      autofocus,
      onCreate: ({ editor: e }) => {
        onEditorReadyRef.current?.(e);
        const empty = isEditorEmpty(e);
        lastEmptyRef.current = empty;
        onEmptyChangeRef.current?.(empty);
      },
      onDestroy: () => onEditorReadyRef.current?.(null),
      onUpdate: ({ editor: e }) => {
        const empty = isEditorEmpty(e);
        if (empty !== lastEmptyRef.current) {
          lastEmptyRef.current = empty;
          onEmptyChangeRef.current?.(empty);
        }
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        getJSON: () => editor?.getJSON() ?? emptyDoc,
        clear: () => {
          editor?.commands.clearContent();
          lastEmptyRef.current = true;
          onEmptyChangeRef.current?.(true);
        },
        isEmpty: () => isEditorEmpty(editor),
      }),
      [editor],
    );

    return (
      <div className={cn('community-editor', className)}>
        <EditorContent editor={editor} className="text-base leading-6 text-black" />
      </div>
    );
  },
);

TiptapEditor.displayName = 'TiptapEditor';

export const CommentEditor = forwardRef<CommentEditorHandle, CommentEditorProps>(
  (
    { placeholder, initialContent, autofocus, className, onEmptyChange, onEditorReady, onKeyboardSubmit, spaceSlug },
    ref,
  ) => {
    return (
      <ClientOnly
        fallback={
          <div className={cn('community-editor text-base leading-6 text-black/30', className)}>{placeholder}</div>
        }
      >
        {() => (
          <TiptapEditor
            ref={ref}
            placeholder={placeholder}
            initialContent={initialContent}
            autofocus={autofocus}
            className={className}
            onEmptyChange={onEmptyChange}
            onEditorReady={onEditorReady}
            onKeyboardSubmit={onKeyboardSubmit}
            spaceSlug={spaceSlug}
          />
        )}
      </ClientOnly>
    );
  },
);

CommentEditor.displayName = 'CommentEditor';
